import SwiperGalleryModal from 'components/Modal/gallery/SwiperGalleryModal'
import ProfileSettingsModal from 'components/Modal/ProfileSettingsModal'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import AddMainPhotoModal from 'components/Modal/AddMainPhotoModal'
import NotificationModal from 'components/Modal/NotificationModal'
import DeleteBoardModal from 'components/Modal/DeleteBoardModal'
import SetPublishModal from 'components/Modal/SetPublishModal'
import AddPhotosModal from 'components/Modal/AddPhotosModal'
import EditDataModal from 'components/Modal/EditDataModal'
import CandlesModal from 'components/Modal/CandlesModal'
import { useEffect, useState } from 'react'
import { GET, POST } from 'api/request'
import TopBar from 'components/Topbar'
import Loader from 'components/Loader'
import TabsManage from './TabsManage'
import * as url from 'api/urls'
import './index.scss'
import BookPublishModal from 'components/Modal/book/BookPublishModal'
import BookDeleteModal from 'components/Modal/book/BookDeleteModal'
import ProfilePasswordModal from 'components/Modal/ProfilePasswordModal'

const ManageProfile: React.FC = () => {
  const [data, setData] = useState<board.BoardResponse | null | undefined>()
  const [isModal, setModal] = useState('')
  const [isLoading, setLoading] = useState<boolean>(true)
  const [initialSlide, setInitialSlide] = useState<number>(0)
  const [activeTab, setActiveTab] = useState('notifications')
  const [retryDelay, setRetryDelay] = useState(5000)
  const [bookId, setBookId] = useState<string>('')

  const [bookFetchLoading, setBookFetchLoading] = useState<boolean>(true)
  const [notificationFetchLoading, setNotificationFetchLoading] = useState<boolean>(true)
  const [notificationRelated, setNotificationRelated] = useState<notifications.Related | null>()
  const [notificationId, setNotificationId] = useState<string>('')

  const [post, setPost] = useState<book.BookEntry[]>([])
  const [postPage, setPostPage] = useState(1)
  const [postHasMore, setPostHasMore] = useState(true)
  const [notifications, setNotifications] = useState<notifications.Notification[]>([])
  const [notificationPage, setNotificationPage] = useState(1)
  const [notificationHasMore, setNotificationHasMore] = useState(true)

  const params = useParams()
  const { pathname } = useLocation()
  const profileId = pathname.split('/manage/')[1]
  const navigate = useNavigate()

  const MAX_DELAY = 80000

  useEffect(() => {
    if (activeTab !== 'photos') return

    const checkPendingStatus = () => {
      const isPending = data?.data?.gallery?.some(media => media.isVideo && (media.cdnStatus === 'PENDING' || media.cdnStatus === 'INIT'))
      if (isPending) {
        if (retryDelay <= MAX_DELAY) {
          const timeout = setTimeout(() => {
            fetchProfile()
            setRetryDelay(prevDelay => Math.min(prevDelay * 2, MAX_DELAY))
          }, retryDelay)
          return () => clearTimeout(timeout)
        }
      } else {
        setRetryDelay(5000)
      }
    }

    checkPendingStatus()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.gallery, activeTab, retryDelay])

  const fetchProfile = async () => {
    setLoading(true)
    try {
      const response = await GET(`${url.boards}/${profileId}`)
      if (response.ok) {
        setData(response.data)
      } else {
        throw new Error('Failed to fetch profile')
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

  const fetchBook = async (page: number = 1) => {
    try {
      const response = await GET(`${url.addPost(profileId)}?page=${page}`)
      if (response.ok) {
        const data: book.BookResponse = response.data
        if (data) {
          if (page === 1) {
            setPost(data.data)
          } else {
            setPost(prevNotifications => [
              ...(prevNotifications || []),
              ...data.data
            ])
          }
          setPostHasMore(data.meta.pagination.current_page < data.meta.pagination.total_pages)
        }
      } else {
        throw new Error('Failed to fetch more post')
      }
    } catch (err) {
      console.error(err)
    } finally {
      setBookFetchLoading(false)
    }
  }

  const fetchNotifications = async (page: number = 1) => {
    try {
      const response = await GET(`${url.notifications(profileId)}?page=${page}`)
      if (response.ok) {
        const data: notifications.NotificationResponse = response.data
        if (data) {
          if (page === 1) {
            setNotifications(data.data)
          } else {
            setNotifications(prevNotifications => [
              ...(prevNotifications || []),
              ...data.data
            ])
          }
          setNotificationHasMore(data.meta.pagination.current_page < data.meta.pagination.total_pages)
        }
      } else {
        throw new Error('Failed to fetch profile')
      }
    } catch (err) {
      console.error(err)
    } finally {
      setNotificationFetchLoading(false)
    }
  }

  const loadMoreNotifications = () => {
    if (notificationHasMore) {
      setNotificationPage(prevPage => prevPage + 1)
      fetchNotifications(notificationPage + 1)
    }
  }

  const loadMorePosts = () => {
    if (postHasMore) {
      setPostPage(prevPage => prevPage + 1)
      fetchBook(postPage + 1)
    }
  }

  useEffect(() => {
    fetchProfile()
    fetchNotifications()
    fetchBook()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params['*']])

  const reloadProfile = () => {
    fetchProfile()
  }

  const reloadNotifications = () => {
    setNotifications([])
    fetchNotifications(notificationPage)
  }

  const reloadBook = () => {
    setPost([])
    fetchBook(notificationPage)
  }

  const reload = () => {
    fetchProfile()
    fetchNotifications(notificationPage)
    fetchBook(postPage)
  }

  const handleUpload = async (id: string) => {
    try {
      const response = await POST(url.uploadYt(id))
      if (response.ok) {
        reloadProfile()
      } else {
        alert('W trakcie wysyłania wystąpił błąd')
      }
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <>
      {isModal === 'candle' && <CandlesModal body={data} onClose={() => setModal('')} onAddCandle={reloadProfile} />}
      {isModal === 'addphotos' && <AddPhotosModal id={data?.data.id} onClose={() => setModal('')} onReload={reloadProfile} />}
      {isModal === 'addMainPhoto' && <AddMainPhotoModal body={data?.data} onClose={() => setModal('')} onReload={reloadProfile} />}
      {isModal === 'editData' && <EditDataModal data={data} onClose={() => setModal('')} onReload={reloadProfile} />}
      {isModal === 'settings' && <ProfileSettingsModal body={data} onClose={() => setModal('')} setModal={setModal} onReload={reload} />}
      {isModal === 'gallery' && data?.data.gallery && <SwiperGalleryModal initialSlide={initialSlide} data={data?.data.gallery} onClose={() => setModal('')} onReload={reloadProfile} />}
      {isModal === 'notificationModal' && <NotificationModal body={notificationRelated} notificationId={notificationId} onClose={() => setModal('')} onReload={reloadNotifications} />}
      {isModal === 'delete' && <DeleteBoardModal boardId={params['*']} onClose={() => setModal('')} onReload={() => navigate('/')} />}
      {isModal === 'publish' && data && <SetPublishModal boardID={data.data.id} isPublish={data.data.is_published} onClose={() => setModal('')} onReload={reload} />}
      {isModal === 'bookPublish' && data && <BookPublishModal bookId={bookId} isPublish={true} onClose={() => setModal('')} onReload={reloadBook} />}
      {isModal === 'bookUnpublish' && data && <BookPublishModal bookId={bookId} isPublish={false} onClose={() => setModal('')} onReload={reloadBook} />}
      {isModal === 'bookDelete' && data && <BookDeleteModal bookId={bookId} onClose={() => setModal('')} onReload={reloadBook} />}
      {isModal === 'password' && data && <ProfilePasswordModal has_password={data?.data.has_password} onClose={() => setModal('')} setProfileData={setData} />}

      {data
        ? <div className="manage">
          <TopBar isSticky={false} theme='dark' title='Zarządzanie profilem' />
          {data.data?.linkedBoards && data.data.linkedBoards.length > 1 && <div className="chooseProfile">
            {data.data?.linkedBoards.map(linked => (
              <div key={linked.id}
                className={`btn btn--round ${linked.id === params['*'] && 'active'}`}
                onClick={() => {
                  navigate(`/manage/${linked.id}`, { replace: true })
                }}>
                {(!linked.is_published || !linked.is_paid) && <i className="icon icon--visibility-off"></i>} {linked.name}
              </div>
            ))}
            <div className="btn btn--round add" onClick={() => navigate(`/addprofile/${data?.data.qr_id}/add`)}>
              <i className="icon icon--qr-code"></i>
              Dodaj osobę do tego kodu QR
            </div>
          </div>}
          <div className="manage__head">
            <div className="wrap">
              <div className="manage__head__left">
                <div className="imgWrap" onClick={() => setModal('addMainPhoto')}>
                  {data?.data.img
                    ? <img src={data?.data.img_thumbnail} alt="" />
                    : <div className='addMain'><span>Kliknij, aby dodać główne zdjęcie</span></div>
                  }
                </div>
              </div>
              <div className="manage__head__right">
                <h1>{data?.data.name}</h1>
                <div className='candle'><i className="icon icon--candle" onClick={() => setModal('candle')}></i>{data?.data.candlesAmount}</div>
                <div className='candle'><i className="icon icon--visibility"></i>{data?.data.entriesAmount}</div>
                <div><div className="btn btn--round" onClick={() => navigate(`/profile/${params['*']}`)}>Podgląd</div></div>
                <div>
                  {data.data.qr_id
                    ? <div
                      className="btn btn--round btn--nopading"
                      onClick={() => setModal('settings')}>
                      <i className="icon icon--settings"></i>
                    </div>
                    : <div
                      className="btn btn--round btn--nopading"
                      onClick={() => setModal('delete')}>
                      <i className="icon icon--trash"></i>
                    </div>}
                </div>
              </div>
            </div>
          </div>
          <div className="manage__tabs">
            <TabsManage
              handleUpload={handleUpload}
              setModal={setModal}
              body={data}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              setInitialSlide={setInitialSlide}
              setNotificationRelated={setNotificationRelated}
              setNotificationId={setNotificationId}
              notificationHasMore={notificationHasMore}
              notifications={notifications}
              post={post}
              postHasMore={postHasMore}
              loadMoreNotifications={loadMoreNotifications}
              loadMorePosts={loadMorePosts}
              setBookId={setBookId} />
          </div>
        </div>
        : (isLoading || notificationFetchLoading || bookFetchLoading)
          ? <Loader type='full' />
          : null}
    </>)
}

export default ManageProfile